<script lang="ts">
  // 2D Pen-tool canvas. Edits either profile (open, vertical) or crossSection
  // (closed, top-down) based on the `mode` prop. Reads/writes app.vaseParams.
  import { onMount, onDestroy } from 'svelte';
  import { app, persistSettings, pushPenEditUndo } from './store.svelte';
  import Icon from './lib/Icon.svelte';

  type PenMode = 'profile' | 'crossSection' | 'pattern';
  let { mode = 'profile' as PenMode }: { mode?: PenMode } = $props();
  import {
    tessellatePath,
    pathBoundingBox,
    profileToPenPath,
    smoothAnchor,
    smoothPath,
    makeAnchor,
    newAnchorId,
    type AnchorKind,
    type AnchorContinuity,
    type PenAnchor,
    type PenPath
  } from '../core/penTool';
  import {
    fitView,
    hitTest,
    screenToWorld,
    worldToScreen,
    snapToGrid,
    applyContinuity,
    cycleContinuity,
    HANDLE_HIT_RADIUS,
    type ViewTransform
  } from './penCanvasInteract';

  let canvas: HTMLCanvasElement | undefined = $state();
  let containerEl: HTMLDivElement | undefined = $state();
  let cw = $state(400);
  let ch = $state(500);

  let nextKind: AnchorKind = $state('line');
  let snapEnabled = $state(true);
  let snapStep = $state(5); // mm
  let selectedIndex: number | null = $state(null);
  let view: ViewTransform = $state({ scale: 4, offsetX: 100, offsetY: 60 });

  type DragKind = 'anchor' | 'cp1' | 'cp2' | 'arcMid' | 'pan' | null;
  let dragging: { kind: DragKind; index: number; startX: number; startY: number; pannedFromOffset?: { x: number; y: number } } | null = null;
  let didMove = false;

  function getPath(): PenPath | null | undefined {
    if (mode === 'crossSection') return app.vaseParams.penCrossSection;
    if (mode === 'pattern') return app.penPattern;
    return app.vaseParams.penProfile;
  }
  function writePath(next: PenPath | null) {
    if (mode === 'crossSection') {
      app.vaseParams = { ...app.vaseParams, penCrossSection: next };
    } else if (mode === 'pattern') {
      app.penPattern = next;
    } else {
      app.vaseParams = { ...app.vaseParams, penProfile: next };
    }
  }

  // Initialize path when entering Pen mode without one set.
  function ensurePath(): PenPath {
    let path = getPath();
    if (!path || path.anchors.length < ((mode === 'crossSection' || mode === 'pattern') ? 3 : 2)) {
      if (mode === 'crossSection' || mode === 'pattern') {
        // Default closed square (4 anchors centered on origin) — mean radius = 1.
        const s = mode === 'pattern' ? 20 : 30;
        path = {
          closed: true,
          anchors: [
            { id: newAnchorId(), z: -s, r: -s, kind: 'line', continuity: 'cusp' },
            { id: newAnchorId(), z: s, r: -s, kind: 'line', continuity: 'cusp' },
            { id: newAnchorId(), z: s, r: s, kind: 'line', continuity: 'cusp' },
            { id: newAnchorId(), z: -s, r: s, kind: 'line', continuity: 'cusp' }
          ]
        };
      } else {
        // Migrate from legacy profilePoints (or 3-point) into a starter line-segment path.
        const legacy = app.vaseParams.profilePoints && app.vaseParams.profilePoints.length >= 2
          ? app.vaseParams.profilePoints
          : [
              { z: 0, r: app.vaseParams.bottomRadius },
              { z: app.vaseParams.height * (app.vaseParams.bellyHeight ?? 0.5), r: app.vaseParams.bellyRadius },
              { z: app.vaseParams.height, r: app.vaseParams.topRadius }
            ];
        path = profileToPenPath(legacy);
      }
      writePath(path);
    }
    return path;
  }

  function commitPath(updater: (p: PenPath) => PenPath) {
    const cur = getPath() ?? ensurePath();
    const next = updater(JSON.parse(JSON.stringify(cur)));
    writePath(next);
  }

  function resize() {
    if (!containerEl || !canvas) return;
    const wrap = canvas.parentElement;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    const newW = Math.max(200, Math.floor(rect.width));
    const newH = Math.max(300, Math.floor(rect.height));
    if (newW === cw && newH === ch && canvas.width === newW * window.devicePixelRatio) return;
    cw = newW;
    ch = newH;
    canvas.width = cw * window.devicePixelRatio;
    canvas.height = ch * window.devicePixelRatio;
    canvas.style.width = cw + 'px';
    canvas.style.height = ch + 'px';
    draw();
  }

  let resizeObs: ResizeObserver | null = null;

  onMount(() => {
    ensurePath();
    if (containerEl) {
      const wrap = containerEl.querySelector('.canvas-wrap') as HTMLElement | null;
      resizeObs = new ResizeObserver((entries) => {
        // rAF debounce — coalesce bursts and break any layout-feedback echo.
        requestAnimationFrame(() => resize());
        void entries;
      });
      if (wrap) resizeObs.observe(wrap);
      else resizeObs.observe(containerEl);
    }
    resize();
    const p = getPath();
    if (p) view = fitView(p, cw, ch);
    draw();
  });

  onDestroy(() => {
    if (resizeObs) resizeObs.disconnect();
  });

  // Reactive redraw on any path/view/state change.
  $effect(() => {
    void app.vaseParams.penProfile; void app.vaseParams.penCrossSection; void app.penPattern;
    void view.scale; void view.offsetX; void view.offsetY;
    void selectedIndex;
    void cw; void ch;
    void app.refPhotoUrl; void app.refPhotoOpacity; void app.showDimensions;
    void mode;
    draw();
  });

  // Publish current selection so VasePanel "Pen seçimini al" button can map
  // anchor indices → Z band for SVG Paint mode.
  $effect(() => {
    if (mode !== 'profile') return;
    const w = window as unknown as { __clayforgePenSelection?: number[] };
    w.__clayforgePenSelection = selectedIndex !== null ? [selectedIndex] : [];
    return () => { (window as unknown as { __clayforgePenSelection?: number[] }).__clayforgePenSelection = []; };
  });

  // Reference photo image cache.
  let refImg: HTMLImageElement | null = null;
  $effect(() => {
    const url = app.refPhotoUrl;
    if (!url) { refImg = null; draw(); return; }
    const img = new Image();
    img.onload = () => { refImg = img; draw(); };
    img.src = url;
  });

  function draw() {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cw, ch);
    // Background
    ctx.fillStyle = '#0a1622';
    ctx.fillRect(0, 0, cw, ch);

    // Reference photo (centered on axis, scaled by refPhotoScale mm/px).
    if (refImg) {
      ctx.save();
      ctx.globalAlpha = app.refPhotoOpacity;
      const imgW = refImg.naturalWidth * app.refPhotoScale * view.scale;
      const imgH = refImg.naturalHeight * app.refPhotoScale * view.scale;
      // Anchor: top of image at z=0, centered horizontally on axis (r=0).
      const x = view.offsetX - imgW / 2;
      const y = ch - view.offsetY - imgH;
      ctx.drawImage(refImg, x, y, imgW, imgH);
      ctx.restore();
    }

    drawGrid(ctx);
    drawAxis(ctx);

    const path = getPath();
    if (path) {
      drawPath(ctx, path);
      drawHandles(ctx, path);
      // Mirror preview only makes sense for the open vase profile (silhouette across axis).
      if (mode === 'profile') drawMirrorPreview(ctx, path);
    }

    if (app.showDimensions && path) drawDimensions(ctx, path);
  }

  function drawGrid(ctx: CanvasRenderingContext2D) {
    const stepPx = snapStep * view.scale;
    if (stepPx < 6) return; // too dense to render
    ctx.strokeStyle = 'rgba(255,255,255,0.04)';
    ctx.lineWidth = 1;
    // vertical lines (constant r)
    const startX = view.offsetX % stepPx;
    for (let x = startX; x < cw; x += stepPx) {
      ctx.beginPath(); ctx.moveTo(x + 0.5, 0); ctx.lineTo(x + 0.5, ch); ctx.stroke();
    }
    for (let x = startX - stepPx; x > 0; x -= stepPx) {
      ctx.beginPath(); ctx.moveTo(x + 0.5, 0); ctx.lineTo(x + 0.5, ch); ctx.stroke();
    }
    // horizontal lines (constant z)
    const baseY = ch - view.offsetY;
    for (let y = baseY; y < ch; y += stepPx) {
      ctx.beginPath(); ctx.moveTo(0, y + 0.5); ctx.lineTo(cw, y + 0.5); ctx.stroke();
    }
    for (let y = baseY - stepPx; y > 0; y -= stepPx) {
      ctx.beginPath(); ctx.moveTo(0, y + 0.5); ctx.lineTo(cw, y + 0.5); ctx.stroke();
    }
  }

  function drawAxis(ctx: CanvasRenderingContext2D) {
    ctx.strokeStyle = 'rgba(120,180,240,0.5)';
    ctx.setLineDash([4, 4]);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(view.offsetX + 0.5, 0);
    ctx.lineTo(view.offsetX + 0.5, ch);
    ctx.stroke();
    ctx.setLineDash([]);
    // baseline z=0
    const y0 = ch - view.offsetY;
    ctx.strokeStyle = 'rgba(120,180,240,0.3)';
    ctx.beginPath();
    ctx.moveTo(0, y0 + 0.5);
    ctx.lineTo(cw, y0 + 0.5);
    ctx.stroke();
  }

  function drawPath(ctx: CanvasRenderingContext2D, path: PenPath) {
    if (path.anchors.length < 2) return;
    const poly = tessellatePath(path, 64);
    ctx.strokeStyle = '#1a7ac9';
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let i = 0; i < poly.length; i++) {
      const s = worldToScreen(poly[i], view, ch);
      if (i === 0) ctx.moveTo(s.x, s.y); else ctx.lineTo(s.x, s.y);
    }
    if (path.closed) ctx.closePath();
    ctx.stroke();
  }

  function drawMirrorPreview(ctx: CanvasRenderingContext2D, path: PenPath) {
    if (path.anchors.length < 2 || path.closed) return;
    const poly = tessellatePath(path, 64);
    ctx.strokeStyle = 'rgba(26,122,201,0.35)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    for (let i = 0; i < poly.length; i++) {
      const s = worldToScreen({ z: poly[i].z, r: -poly[i].r }, view, ch);
      if (i === 0) ctx.moveTo(s.x, s.y); else ctx.lineTo(s.x, s.y);
    }
    ctx.stroke();
  }

  function drawHandles(ctx: CanvasRenderingContext2D, path: PenPath) {
    for (let i = 0; i < path.anchors.length; i++) {
      const a = path.anchors[i];
      const s = worldToScreen({ z: a.z, r: a.r }, view, ch);
      const isSel = selectedIndex === i;

      // Control-point handles + dotted lines for selected anchor.
      if (isSel) {
        ctx.strokeStyle = 'rgba(255,200,100,0.6)';
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        if ((a.kind === 'cubic' || a.kind === 'quad') && a.cp1) {
          const c = worldToScreen(a.cp1, view, ch);
          ctx.beginPath(); ctx.moveTo(s.x, s.y); ctx.lineTo(c.x, c.y); ctx.stroke();
          drawCircle(ctx, c.x, c.y, 5, '#ffc864');
        }
        if (a.kind === 'cubic' && a.cp2) {
          const c = worldToScreen(a.cp2, view, ch);
          ctx.beginPath(); ctx.moveTo(s.x, s.y); ctx.lineTo(c.x, c.y); ctx.stroke();
          drawCircle(ctx, c.x, c.y, 5, '#ffc864');
        }
        if (a.kind === 'arc' && a.arcMid) {
          const c = worldToScreen(a.arcMid, view, ch);
          drawCircle(ctx, c.x, c.y, 5, '#9ee04a');
        }
        ctx.setLineDash([]);
      }

      // Anchor shape by continuity.
      const cont = a.continuity ?? 'smooth';
      const color = isSel ? '#ffd166' : '#bfe6ff';
      if (cont === 'cusp') drawSquare(ctx, s.x, s.y, 8, color);
      else if (cont === 'symmetric') drawDiamond(ctx, s.x, s.y, 8, color);
      else drawCircle(ctx, s.x, s.y, 5, color);
    }
  }

  function drawCircle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, fill: string) {
    ctx.fillStyle = fill;
    ctx.strokeStyle = '#0a1622';
    ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill(); ctx.stroke();
  }
  function drawSquare(ctx: CanvasRenderingContext2D, x: number, y: number, s: number, fill: string) {
    ctx.fillStyle = fill;
    ctx.strokeStyle = '#0a1622';
    ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.rect(x - s / 2, y - s / 2, s, s); ctx.fill(); ctx.stroke();
  }
  function drawDiamond(ctx: CanvasRenderingContext2D, x: number, y: number, s: number, fill: string) {
    ctx.fillStyle = fill;
    ctx.strokeStyle = '#0a1622';
    ctx.lineWidth = 1.5;
    const r = s / 1.4;
    ctx.beginPath();
    ctx.moveTo(x, y - r); ctx.lineTo(x + r, y);
    ctx.lineTo(x, y + r); ctx.lineTo(x - r, y); ctx.closePath();
    ctx.fill(); ctx.stroke();
  }

  function drawDimensions(ctx: CanvasRenderingContext2D, path: PenPath) {
    const bb = pathBoundingBox(path);
    const height = Math.max(0, bb.zMax - bb.zMin);
    const maxR = Math.max(Math.abs(bb.rMin), Math.abs(bb.rMax));
    const maxDia = maxR * 2;
    ctx.fillStyle = 'rgba(7,17,26,0.85)';
    ctx.fillRect(8, 8, 180, 60);
    ctx.fillStyle = '#cfe5ff';
    ctx.font = '11px Inter, system-ui, sans-serif';
    ctx.fillText(`Yükseklik: ${height.toFixed(1)} mm`, 16, 24);
    ctx.fillText(`Maks Çap: ${maxDia.toFixed(1)} mm`, 16, 40);
    ctx.fillText(`Anchor: ${path.anchors.length}`, 16, 56);
  }

  // ---- Pointer events ----

  function localPos(e: PointerEvent): { x: number; y: number } {
    const rect = canvas!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function onPointerDown(e: PointerEvent) {
    if (!canvas) return;
    const p = localPos(e);
    const path = ensurePath();
    didMove = false;

    // Middle-button or space-pan
    if (e.button === 1 || e.metaKey) {
      dragging = { kind: 'pan', index: -1, startX: p.x, startY: p.y, pannedFromOffset: { x: view.offsetX, y: view.offsetY } };
      canvas.setPointerCapture(e.pointerId);
      return;
    }

    const hit = hitTest(p.x, p.y, path, view, ch, selectedIndex);

    // Right-click or alt-click to delete
    if (e.button === 2 || e.altKey) {
      if (hit && hit.kind === 'anchor' && path.anchors.length > 2) {
        pushPenEditUndo();
        commitPath((cur) => {
          cur.anchors.splice(hit.index, 1);
          return cur;
        });
        selectedIndex = null;
      }
      e.preventDefault();
      return;
    }

    if (hit) {
      // Drag existing handle / anchor
      pushPenEditUndo();
      dragging = { kind: hit.kind, index: hit.index, startX: p.x, startY: p.y };
      if (hit.kind === 'anchor') selectedIndex = hit.index;
      canvas.setPointerCapture(e.pointerId);
      return;
    }

    // Click empty space → add anchor at cursor (insert near closest segment).
    pushPenEditUndo();
    const world = screenToWorld(p.x, p.y, view, ch);
    const snapped = snapEnabled ? snapToGrid(world, snapStep) : world;
    const newAnchor: PenAnchor = makeAnchor(snapped.z, Math.max(0.5, snapped.r), nextKind);

    // Insert ordering depends on mode: profile = ascending z; crossSection = angular.
    commitPath((cur) => {
      let insertAt = cur.anchors.length;
      if (mode === 'crossSection' || mode === 'pattern') {
        // Angular: place by atan2 around centroid for a sane closed-polygon order.
        let cx = 0, cy = 0;
        for (const a of cur.anchors) { cx += a.z; cy += a.r; }
        cx /= Math.max(1, cur.anchors.length); cy /= Math.max(1, cur.anchors.length);
        const newAng = Math.atan2(newAnchor.r - cy, newAnchor.z - cx);
        const angles = cur.anchors.map((a) => Math.atan2(a.r - cy, a.z - cx));
        for (let i = 0; i < angles.length; i++) {
          if (angles[i] > newAng) { insertAt = i; break; }
        }
      } else {
        for (let i = 0; i < cur.anchors.length; i++) {
          if (cur.anchors[i].z > newAnchor.z) { insertAt = i; break; }
        }
      }
      cur.anchors.splice(insertAt, 0, newAnchor);
      // For Bezier kinds we need control points relative to neighbors.
      if (newAnchor.kind === 'cubic' && insertAt > 0 && insertAt < cur.anchors.length - 1) {
        const prev = cur.anchors[insertAt - 1];
        newAnchor.cp1 = { z: (prev.z + newAnchor.z) * 0.6, r: (prev.r + newAnchor.r) * 0.5 };
        newAnchor.cp2 = { z: (prev.z + newAnchor.z) * 0.4, r: (prev.r + newAnchor.r) * 0.5 };
      } else if (newAnchor.kind === 'quad' && insertAt > 0) {
        const prev = cur.anchors[insertAt - 1];
        newAnchor.cp1 = { z: (prev.z + newAnchor.z) * 0.5, r: (prev.r + newAnchor.r) * 0.5 + 5 };
      } else if (newAnchor.kind === 'arc' && insertAt > 0) {
        const prev = cur.anchors[insertAt - 1];
        newAnchor.arcMid = { z: (prev.z + newAnchor.z) * 0.5, r: (prev.r + newAnchor.r) * 0.5 + 5 };
      }
      return cur;
    });
    selectedIndex = (getPath()?.anchors ?? []).findIndex((a) => a.id === newAnchor.id);
  }

  function onPointerMove(e: PointerEvent) {
    if (!dragging || !canvas) return;
    const p = localPos(e);
    if (!didMove && Math.hypot(p.x - dragging.startX, p.y - dragging.startY) > 2) didMove = true;
    if (dragging.kind === 'pan' && dragging.pannedFromOffset) {
      view = {
        ...view,
        offsetX: dragging.pannedFromOffset.x + (p.x - dragging.startX),
        offsetY: dragging.pannedFromOffset.y - (p.y - dragging.startY)
      };
      return;
    }
    const world = screenToWorld(p.x, p.y, view, ch);
    let pos: { z: number; r: number } = world;
    if (snapEnabled && !e.shiftKey) pos = snapToGrid(world, snapStep);
    if (e.shiftKey) {
      // Axis lock: keep the larger movement axis only relative to drag start.
      const startWorld = screenToWorld(dragging.startX, dragging.startY, view, ch);
      const dz = Math.abs(pos.z - startWorld.z);
      const dr = Math.abs(pos.r - startWorld.r);
      if (dz > dr) pos = { z: pos.z, r: startWorld.r };
      else pos = { z: startWorld.z, r: pos.r };
    }
    commitPath((cur) => {
      const a = cur.anchors[dragging!.index];
      if (!a) return cur;
      if (dragging!.kind === 'anchor') {
        a.z = pos.z; a.r = Math.max(0.5, pos.r);
      } else if (dragging!.kind === 'cp1') {
        a.cp1 = { z: pos.z, r: pos.r };
        applyContinuity(cur, dragging!.index - 1);
      } else if (dragging!.kind === 'cp2') {
        a.cp2 = { z: pos.z, r: pos.r };
        applyContinuity(cur, dragging!.index);
      } else if (dragging!.kind === 'arcMid') {
        a.arcMid = { z: pos.z, r: pos.r };
      }
      return cur;
    });
  }

  function onPointerUp(e: PointerEvent) {
    if (!dragging) return;
    if (canvas) canvas.releasePointerCapture(e.pointerId);
    dragging = null;
  }

  function onDoubleClick(e: MouseEvent) {
    if (!canvas) return;
    const path = getPath();
    if (!path) return;
    const rect = canvas.getBoundingClientRect();
    const p = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    const hit = hitTest(p.x, p.y, path, view, ch, selectedIndex);
    if (hit && hit.kind === 'anchor') {
      pushPenEditUndo();
      commitPath((cur) => {
        const a = cur.anchors[hit.index];
        a.continuity = cycleContinuity(a.continuity);
        return cur;
      });
    }
  }

  function onWheel(e: WheelEvent) {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const worldBefore = screenToWorld(px, py, view, ch);
    const newScale = Math.max(0.5, Math.min(50, view.scale * factor));
    view = {
      scale: newScale,
      offsetX: px - worldBefore.r * newScale,
      offsetY: ch - py - worldBefore.z * newScale
    };
  }

  // Reference-photo upload.
  let fileInput: HTMLInputElement | undefined = $state();
  function onPhotoFile(e: Event) {
    const f = (e.target as HTMLInputElement).files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => { app.refPhotoUrl = reader.result as string; };
    reader.readAsDataURL(f);
  }
  function clearPhoto() { app.refPhotoUrl = null; }

  function fitNow() {
    const path = getPath();
    if (!path) return;
    view = fitView(path, cw, ch);
  }

  function setKind(k: AnchorKind) {
    nextKind = k;
    // Also retype the currently-selected anchor when one is selected.
    if (selectedIndex !== null) {
      pushPenEditUndo();
      commitPath((cur) => {
        const a = cur.anchors[selectedIndex!];
        if (!a) return cur;
        a.kind = k;
        if (k === 'cubic' && (!a.cp1 || !a.cp2) && selectedIndex! > 0) {
          const prev = cur.anchors[selectedIndex! - 1];
          a.cp1 = { z: prev.z + (a.z - prev.z) * 0.33, r: prev.r + (a.r - prev.r) * 0.33 };
          a.cp2 = { z: prev.z + (a.z - prev.z) * 0.66, r: prev.r + (a.r - prev.r) * 0.66 };
        } else if (k === 'quad' && !a.cp1 && selectedIndex! > 0) {
          const prev = cur.anchors[selectedIndex! - 1];
          a.cp1 = { z: (prev.z + a.z) * 0.5, r: (prev.r + a.r) * 0.5 };
        } else if (k === 'arc' && !a.arcMid && selectedIndex! > 0) {
          const prev = cur.anchors[selectedIndex! - 1];
          a.arcMid = { z: (prev.z + a.z) * 0.5, r: (prev.r + a.r) * 0.5 };
        }
        return cur;
      });
    }
  }

  // ---- Extra tools ----

  /** Smoothing strength 0..1 (typical 0.25). User-adjustable, persisted as session setting. */
  let smoothStrength = $state(0.3);

  /** Smooth currently-selected anchor (or whole path if no selection). */
  function smoothSelected() {
    pushPenEditUndo();
    commitPath((cur) => {
      if (selectedIndex !== null && selectedIndex >= 0) {
        smoothAnchor(cur, selectedIndex, smoothStrength);
      } else {
        smoothPath(cur, smoothStrength);
      }
      return cur;
    });
  }

  /** Toggle whether the path is closed (loop) or open. Only meaningful for
   *  pattern/cross-section modes — profile mode is forced open by mesh logic. */
  function toggleClosed() {
    if (mode === 'profile') return;
    pushPenEditUndo();
    commitPath((cur) => { cur.closed = !cur.closed; return cur; });
  }

  /** Clear all anchors back to the default starter for the current mode. */
  function clearAll() {
    pushPenEditUndo();
    writePath(null);
    selectedIndex = null;
    ensurePath();
  }

  /** Mirror the current path across the axis (profile: r-axis at 0,
   *  closed: centroid). Useful for symmetric vase silhouettes. */
  function mirrorPath() {
    pushPenEditUndo();
    commitPath((cur) => {
      let cx = 0;
      if (mode === 'profile') {
        cx = 0; // mirror across r=0
        for (const a of cur.anchors) {
          a.r = -a.r;
          if (a.cp1) a.cp1.r = -a.cp1.r;
          if (a.cp2) a.cp2.r = -a.cp2.r;
          if (a.arcMid) a.arcMid.r = -a.arcMid.r;
        }
      } else {
        // Mirror across centroid X
        for (const a of cur.anchors) cx += a.z;
        cx /= cur.anchors.length;
        for (const a of cur.anchors) {
          a.z = 2 * cx - a.z;
          if (a.cp1) a.cp1.z = 2 * cx - a.cp1.z;
          if (a.cp2) a.cp2.z = 2 * cx - a.cp2.z;
          if (a.arcMid) a.arcMid.z = 2 * cx - a.arcMid.z;
        }
      }
      return cur;
    });
  }

  /** Evenly redistribute anchors along the current tessellated path so they sit
   *  at uniform arc-length intervals. Preserves first/last endpoints in open paths. */
  function distributeAnchors() {
    const path = getPath();
    if (!path || path.anchors.length < 3) return;
    pushPenEditUndo();
    const poly = tessellatePath(path, 256);
    const n = path.anchors.length;
    // Cumulative arc length
    const cum = [0];
    for (let i = 1; i < poly.length; i++) {
      const dz = poly[i].z - poly[i - 1].z;
      const dr = poly[i].r - poly[i - 1].r;
      cum.push(cum[i - 1] + Math.hypot(dz, dr));
    }
    const total = cum[cum.length - 1];
    if (total < 1e-6) return;
    commitPath((cur) => {
      for (let i = 0; i < n; i++) {
        const target = (i / (n - 1)) * total;
        let j = 0;
        while (j < cum.length - 2 && cum[j + 1] < target) j++;
        const t = (target - cum[j]) / Math.max(1e-9, cum[j + 1] - cum[j]);
        const a = cur.anchors[i];
        a.z = poly[j].z + (poly[j + 1].z - poly[j].z) * t;
        a.r = poly[j].r + (poly[j + 1].r - poly[j].r) * t;
        // Reset to line — distribution invalidates Bezier handles.
        a.kind = 'line';
        a.cp1 = undefined;
        a.cp2 = undefined;
        a.arcMid = undefined;
      }
      return cur;
    });
  }
</script>

<div class="pen-canvas" bind:this={containerEl}>
  <div class="toolbar">
    <div class="group kind">
      <button class="chip k" class:active={nextKind === 'line'} onclick={() => setKind('line')} title="Line — düz segment">
        <Icon name="pen-line" size={12} /> Line
      </button>
      <button class="chip k" class:active={nextKind === 'quad'} onclick={() => setKind('quad')} title="Quadratic Bezier — tek tutamak">
        <Icon name="spline" size={12} /> Quad
      </button>
      <button class="chip k" class:active={nextKind === 'cubic'} onclick={() => setKind('cubic')} title="Cubic Bezier — iki tutamak (yumuşak)">
        <Icon name="waves" size={12} /> Cubic
      </button>
      <button class="chip k" class:active={nextKind === 'arc'} onclick={() => setKind('arc')} title="3-nokta yay">
        <Icon name="circle-dot" size={12} /> Arc
      </button>
    </div>
    <div class="group">
      <label class="snap">
        <input type="checkbox" bind:checked={snapEnabled} />
        <Icon name="grid" size={12} /> Snap
      </label>
      <input type="number" min="0.5" max="20" step="0.5" bind:value={snapStep} class="num" title="Grid step (mm)" />
    </div>
    <div class="group">
      <button class="chip icon" onclick={fitNow} title="Görünümü sığdır">
        <Icon name="maximize-2" size={12} />
      </button>
      <button class="chip icon" onclick={() => (app.showDimensions = !app.showDimensions)}
        class:active={app.showDimensions} title="Ölçü göster">
        <Icon name="info" size={12} />
      </button>
    </div>
  </div>
  <div class="toolbar-row2">
    <button class="chip small" onclick={smoothSelected}
      title={selectedIndex !== null ? 'Seçili noktayı yumuşat (cubic + tangent handles)' : 'Tüm noktaları yumuşat'}>
      <Icon name="waves" size={11} /> Yumuşat
    </button>
    <input type="range" min="0.05" max="0.5" step="0.01" bind:value={smoothStrength}
      class="strength" title="Yumuşatma şiddeti" />
    <span class="strength-val">{smoothStrength.toFixed(2)}</span>
    <span class="divider"></span>
    <button class="chip small" onclick={distributeAnchors}
      disabled={(getPath()?.anchors.length ?? 0) < 3}
      title="Noktaları yol uzunluğunca eşit dağıt">
      <Icon name="fold-vertical" size={11} /> Eşitle
    </button>
    <button class="chip small" onclick={mirrorPath}
      disabled={(getPath()?.anchors.length ?? 0) < 2}
      title={mode === 'profile' ? 'Profili eksene göre aynala (R = -R)' : 'Yatay aynalama'}>
      <Icon name="flip" size={11} /> Aynala
    </button>
    {#if mode !== 'profile'}
      <button class="chip small" onclick={toggleClosed}
        class:active={getPath()?.closed}
        title="Yolu kapat / aç">
        <Icon name="square-dashed-bottom" size={11} />
        {getPath()?.closed ? 'Açık yap' : 'Kapat'}
      </button>
    {/if}
    <span class="divider"></span>
    <button class="chip small danger" onclick={clearAll} title="Tüm noktaları sil + varsayılana dön">
      <Icon name="trash" size={11} /> Temizle
    </button>
  </div>
  <div class="canvas-wrap">
    <canvas
      bind:this={canvas}
      onpointerdown={onPointerDown}
      onpointermove={onPointerMove}
      onpointerup={onPointerUp}
      ondblclick={onDoubleClick}
      onwheel={onWheel}
      oncontextmenu={(e) => e.preventDefault()}
    ></canvas>
  </div>
  <div class="ref-photo-row">
    <button class="chip small" onclick={() => fileInput?.click()}>
      <Icon name="camera" size={12} /> Foto Yükle
    </button>
    <input type="file" accept="image/*" bind:this={fileInput} onchange={onPhotoFile} style="display:none" />
    {#if app.refPhotoUrl}
      <label class="opacity">
        Opaklık
        <input type="range" min="0" max="1" step="0.05"
          value={app.refPhotoOpacity}
          oninput={(e) => { app.refPhotoOpacity = parseFloat((e.currentTarget as HTMLInputElement).value); persistSettings(); }} />
      </label>
      <label class="opacity">
        mm/px
        <input type="number" min="0.01" max="100" step="0.05"
          value={app.refPhotoScale}
          oninput={(e) => { app.refPhotoScale = parseFloat((e.currentTarget as HTMLInputElement).value); persistSettings(); }}
          class="num" />
      </label>
      <button class="chip small danger" onclick={clearPhoto}>
        <Icon name="trash" size={11} /> Sil
      </button>
    {/if}
  </div>
  {#if selectedIndex !== null && getPath()?.anchors[selectedIndex]}
    {@const a = getPath()!.anchors[selectedIndex]}
    <div class="anchor-inspector">
      <span class="muted">Anchor #{selectedIndex}</span>
      <span class="muted">{a.kind}</span>
      <span class="muted">{a.continuity ?? 'smooth'}</span>
      <span class="muted">z={a.z.toFixed(1)} r={a.r.toFixed(1)}</span>
      <span class="hint">çift-tık → continuity</span>
    </div>
  {/if}
</div>

<style>
  .pen-canvas {
    display: flex; flex-direction: column;
    height: 100%; width: 100%; min-width: 0; min-height: 0;
    gap: 6px;
    padding: 6px;
    box-sizing: border-box;
    background: var(--panel-overlay, rgba(255,255,255,0.02));
    border: 1px solid var(--border-soft, rgba(255,255,255,0.05));
    border-radius: 8px;
    overflow: hidden;
  }
  .toolbar { display: flex; gap: 6px; flex-wrap: wrap; align-items: center; }
  .toolbar-row2 {
    display: flex; gap: 5px; align-items: center; flex-wrap: wrap;
    padding: 6px 8px;
    background: rgba(255,255,255,0.025);
    border: 1px solid var(--border-soft);
    border-radius: 8px;
  }
  .toolbar-row2 .divider {
    width: 1px; height: 16px;
    background: rgba(255,255,255,0.08);
    margin: 0 3px;
  }
  .toolbar-row2 .strength {
    width: 80px; accent-color: var(--accent);
  }
  .toolbar-row2 .strength-val {
    color: var(--text-2); font-size: 10.5px; font-variant-numeric: tabular-nums;
    min-width: 28px;
  }
  .toolbar-row2 .chip[disabled] { opacity: 0.4; cursor: not-allowed; }
  .group {
    display: flex; gap: 3px; align-items: center;
    padding: 3px;
    background: rgba(255,255,255,0.025);
    border: 1px solid var(--border-soft);
    border-radius: 8px;
  }
  .group.kind .chip { font-weight: 600; }
  .canvas-wrap {
    flex: 1; min-height: 0; min-width: 0;
    border-radius: 8px; overflow: hidden;
    position: relative;
    border: 1px solid var(--border-soft);
    box-shadow: 0 8px 24px -16px rgba(0,0,0,0.5) inset;
  }
  canvas {
    position: absolute; inset: 0;
    display: block; cursor: crosshair; touch-action: none;
  }
  .chip {
    display: inline-flex; align-items: center; gap: 4px;
    background: transparent; color: var(--text-2);
    padding: 5px 10px;
    border: 1px solid transparent; border-radius: 6px;
    font-size: 11px; font-weight: 500;
    cursor: pointer; font-family: inherit;
    transition: background 0.12s, color 0.12s, border-color 0.12s;
  }
  .chip:hover { background: rgba(255,255,255,0.06); color: var(--text-bright); }
  .chip.active {
    background: linear-gradient(180deg, rgba(26,122,201,0.32), rgba(26,122,201,0.18));
    color: #d6ecff;
    border-color: rgba(74,184,255,0.4);
    box-shadow: 0 0 0 1px rgba(74,184,255,0.18) inset;
  }
  .chip.icon { padding: 5px 8px; }
  .chip.small { padding: 4px 9px; font-size: 10.5px; }
  .chip.danger { background: rgba(208,74,58,0.15); color: #ffaaa0; border-color: rgba(208,74,58,0.35); }
  .chip.danger:hover { background: rgba(208,74,58,0.28); }
  .num {
    width: 60px; padding: 4px 7px; font-size: 11px;
    background: var(--bg-2, #16161d); color: var(--text-1, #e6eef6);
    border: 1px solid var(--border, rgba(255,255,255,0.05)); border-radius: 5px; font-family: inherit;
    transition: border-color 0.12s;
  }
  .num:focus { outline: none; border-color: var(--accent); }
  .snap {
    display: inline-flex; align-items: center; gap: 5px;
    color: var(--text-2); font-size: 11px;
    padding: 0 4px;
  }
  .snap input[type="checkbox"] { accent-color: var(--accent); margin: 0; }
  .ref-photo-row { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; padding: 4px 0; }
  .opacity { display: inline-flex; align-items: center; gap: 5px; color: var(--text-3); font-size: 10.5px; }
  .opacity input[type=range] { width: 80px; accent-color: var(--accent); }
  .anchor-inspector {
    display: flex; gap: 10px; padding: 6px 10px; font-size: 11px;
    background: linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01));
    border: 1px solid var(--border-soft);
    border-radius: 8px; align-items: center; flex-wrap: wrap;
  }
  .muted { color: var(--text-muted); }
  .hint { color: var(--text-muted); margin-left: auto; font-style: italic; }
</style>
